import { useCallback, useRef, useState } from "react";

export type MuiMenu<T extends Element = Element> = [
  React.RefObject<T>,
  {
    readonly anchorEl: T | null;
    readonly onClose: () => void;
    readonly open: boolean;
  },
  {
    readonly open: boolean;
    hide: VoidFunction;
    show: VoidFunction;
    toggle: VoidFunction;
    anchor: T | null;
  },
];

export function useMuiMenu<T extends Element = Element>(
  initialOpen: boolean | (() => boolean) = false,
): MuiMenu<T> {
  const ref = useRef<T>(null);
  const [open, setOpen] = useState<boolean>(initialOpen);

  const hide = useCallback(() => setOpen(false), [setOpen]);
  const show = useCallback(() => setOpen(true), [setOpen]);
  const toggle = useCallback(() => setOpen((prev) => !prev), [setOpen]);

  return [
    ref as React.RefObject<T>,
    {
      anchorEl: ref.current,
      onClose: hide,
      open,
    },
    {
      open,
      hide,
      show,
      toggle,
      anchor: ref.current,
    },
  ];
}
